import React from 'react';
import { TrendingUp, ShieldCheck, Sparkles, Target, BarChart3 } from 'lucide-react';

const features = [
  {
    title: 'Complete financial statement view',
    description:
      'Track income, expenses, assets, and liabilities in one dashboard inspired by Rich Dad-style cashflow statements.',
    icon: BarChart3,
  },
  {
    title: 'Passive income milestones',
    description:
      'Real-time ratios show how close your passive income is to covering monthly expenses so you can exit the rat race faster.',
    icon: TrendingUp,
  },
  {
    title: 'Secure Google login',
    description: 'Sign in with Google and keep your financial data protected with Firebase authentication.',
    icon: ShieldCheck,
  },
];

const steps = [
  'Create a free account with Google sign-in.',
  'Add your salary, passive income sources, and recurring expenses.',
  'Log assets such as stocks, funds, and rental properties, plus their cashflow.',
  'Enter liabilities to auto-calculate payments and update your expense line.',
  'Use the summary screen to decide the next investment move and monitor progress weekly.',
];

const faqs = [
  {
    question: 'Is Financial Statement Tracker free to use?',
    answer: 'Yes. The web app is completely free right now so you can log in and start tracking without a subscription.',
  },
  {
    question: 'Can I use it on mobile?',
    answer:
      'The responsive layout works on any device. Install it as a PWA from your browser for a native-like experience.',
  },
  {
    question: 'How is my data stored?',
    answer: 'Your financial entries are saved securely in Firebase, and you always control what you add or delete.',
  },
];

export default function MarketingContent() {
  return (
    <section
      className="bg-white/80 backdrop-blur rounded-2xl shadow-xl p-6 md:p-8 border border-purple-100"
      aria-label="Financial Statement Tracker overview"
    >
      <p className="inline-flex items-center text-xs uppercase tracking-widest text-purple-700 bg-purple-100 px-3 py-1 rounded-full mb-4">
        <Sparkles className="w-4 h-4 mr-2" />
        Financial statement tracker
      </p>
      <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-4">
        Track cashflow, raise passive income, and optimise every statement.
      </h1>
      <p className="text-gray-600 mb-8">
        The Financial Statement Tracker is a focused personal finance tool built for ambitious professionals. It
        mirrors the financial statement from the Cashflow board game—so you can see exactly how your salary,
        dividends, rental cashflow, and liabilities interact before you make your next investment move.
      </p>

      <div className="space-y-5 mb-8">
        {features.map(({ title, description, icon: Icon }) => (
          <div key={title} className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-purple-50 text-purple-700">
              <Icon className="w-5 h-5" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              <p className="text-gray-600 text-sm">{description}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-r from-amber-50 to-purple-50 border border-amber-200 rounded-2xl p-5 mb-8">
        <div className="flex items-center gap-3 mb-3 text-purple-900 font-semibold uppercase tracking-widest text-xs">
          <Target className="w-4 h-4" />
          How it works
        </div>
        <ol className="list-decimal pl-6 space-y-2 text-sm text-gray-700">
          {steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
        <h2 className="text-xl font-bold text-gray-900">Popular questions</h2>
        {faqs.map(({ question, answer }) => (
          <div key={question}>
            <p className="font-semibold text-gray-800">{question}</p>
            <p className="text-sm text-gray-600">{answer}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

